import type * as monaco from "monaco-editor";

import { defaultOptions, Tokenizer } from "liquidjs";

import { liquidTemplatePlaceholderRegex } from "./regex-definitions";

export const liquidTemplateTokenLegend: monaco.languages.SemanticTokensLegend =
  {
    tokenModifiers: [],
    tokenTypes: ["entityname", "liquidtemplate"], // Only one type
  };

// Extended legend for detailed liquid template syntax highlighting
export const liquidSyntaxTokenLegend: monaco.languages.SemanticTokensLegend = {
  tokenModifiers: [],
  tokenTypes: [
    "entityname", // 0 - entity placeholders << >>
    "liquidstring", // 1 - strings in liquid templates
    "liquidvariable", // 2 - variables/identifiers
    "liquidfilter", // 3 - filter names
    "liquidnumber", // 4 - numbers
    "liquidoperator", // 5 - operators | and :
    "liquidproperty", // 6 - property access .
    "liquidbraces", // 7 - braces {{ and }}
  ],
};

type RegexTokenConfig = {
  regex: RegExp;
  tokenType: number;
};

type LiquidToken = {
  index: number;
  length: number;
  text: string;
  tokenType: number;
};

export const liquidTokensWithLiquidJs = (
  content: string,
  baseIndex: number,
): LiquidToken[] => {
  try {
    const tokenizer = new Tokenizer(content, defaultOptions.operators);
    const out: LiquidToken[] = [];

    const push = (label: string, t: { begin: number; end: number }): void => {
      out.push({
        index: baseIndex + t.begin + 1,
        length: t.end - t.begin,
        text: content.slice(t.begin, t.end),
        tokenType: liquidSyntaxTokenLegend.tokenTypes.indexOf(label),
      });
    };

    while (!tokenizer.end()) {
      const before = tokenizer.p;

      tokenizer.skipBlank();

      const ch = content[tokenizer.p];
      if (ch === "|" || ch === ":") {
        push("liquidoperator", { begin: tokenizer.p, end: tokenizer.p + 1 }); // liquidoperator
        tokenizer.advance(1);
        continue;
      }

      const op = tokenizer.readOperator();
      if (op) {
        push("liquidoperator", op);
        continue;
      }

      const quoted = tokenizer.readQuoted();
      if (quoted) {
        push("liquidstring", quoted);
        continue;
      }

      const num = tokenizer.readNumber();
      if (num) {
        push("liquidnumber", num);
        continue;
      }

      const ident = tokenizer.readNonEmptyIdentifier();
      if (ident) {
        let j = ident.begin - 1;
        while (j >= 0 && /\s/.test(content[j])) j--;
        const isFilterName = j >= 0 && content[j] === "|";
        push(isFilterName ? "liquidfilter" : "liquidvariable", ident);
        continue;
      }

      if (tokenizer.p === before) tokenizer.advance(1);
    }
    return out;
  } catch (error) {
    console.error("Error tokenizing liquid template", error);
    return [];
  }
};

/**
 * Creates a semantic token provider that highlights liquid template syntax
 * inside {{ }} blocks with detailed syntax highlighting
 */
export const createLiquidSyntaxTokenProvider = (
  entityRegex: RegExp,
): monaco.languages.DocumentSemanticTokensProvider => ({
  getLegend: (): monaco.languages.SemanticTokensLegend =>
    liquidSyntaxTokenLegend,
  provideDocumentSemanticTokens: (
    model: monaco.editor.ITextModel,
  ): monaco.languages.SemanticTokens => {
    const text = model.getValue();
    const tokens: LiquidToken[] = [];

    // First, find and tokenize entity placeholders
    const entityRegexInstance = new RegExp(
      entityRegex.source,
      entityRegex.flags,
    );
    let match;
    entityRegexInstance.lastIndex = 0;
    while ((match = entityRegexInstance.exec(text)) !== null) {
      tokens.push({
        index: match.index,
        length: match[0].length,
        text: match[0],
        tokenType: 0, // entityname
      });
    }

    // Then, find and parse liquid templates
    const liquidRegexInstance = new RegExp(
      liquidTemplatePlaceholderRegex.source,
      liquidTemplatePlaceholderRegex.flags,
    );
    liquidRegexInstance.lastIndex = 0;
    while ((match = liquidRegexInstance.exec(text)) !== null) {
      const fullMatch = match[0]; // {{ ... }}
      const matchIndex = match.index;
      const content = match[1] || ""; // Content inside {{ }}
      const contentStartIndex = matchIndex + 2; // Skip {{
      // Closing }} is at the end of the full match, 2 characters before the end
      const closingBraceIndex = matchIndex + fullMatch.length - 2;

      // Add token for opening braces {{
      tokens.push({
        index: matchIndex,
        length: 2,
        text: "{{",
        tokenType: 7, // liquidbraces
      });

      // Parse the content inside {{ }}
      const liquidTokens = liquidTokensWithLiquidJs(content, contentStartIndex);

      tokens.push(...liquidTokens);

      // Add token for closing braces }}
      tokens.push({
        index: closingBraceIndex,
        length: 2,
        text: "}}",
        tokenType: 7, // liquidbraces
      });
    }

    // Sort tokens by index
    tokens.sort((a, b) => a.index - b.index);

    // Convert to Monaco token format
    const monacoTokens = [];
    let prevLine = 0;
    let prevChar = 0;

    for (const token of tokens) {
      const startPos = model.getPositionAt(token.index);

      // Calculate relative positions
      const line = startPos.lineNumber - 1; // 0-based line number
      const deltaLine = line - prevLine;
      const startChar = startPos.column - 1; // 0-based column
      const deltaStartChar =
        line === prevLine ? startChar - prevChar : startChar;

      monacoTokens.push({
        deltaLine,
        deltaStartChar,
        length: token.length,
        tokenModifiers: 0,
        tokenType: token.tokenType,
      });

      // Update previous positions
      prevLine = line;
      prevChar = startChar;
    }

    const data = monacoTokens.flatMap(
      ({ deltaLine, deltaStartChar, length, tokenModifiers, tokenType }) => [
        deltaLine,
        deltaStartChar,
        length,
        tokenType,
        tokenModifiers,
      ],
    );

    return {
      data: new Uint32Array(data),
    };
  },
  releaseDocumentSemanticTokens: (): void => {},
});

export const createSemanticTokenProvider = (
  regexes: RegexTokenConfig[],
  legend: monaco.languages.SemanticTokensLegend,
): monaco.languages.DocumentSemanticTokensProvider => ({
  getLegend: (): monaco.languages.SemanticTokensLegend => legend,
  provideDocumentSemanticTokens: (
    model: monaco.editor.ITextModel,
  ): monaco.languages.SemanticTokens => {
    const text = model.getValue();
    const matches: Array<{
      index: number;
      length: number;
      tokenType: number; // 0 for entityname, 1 for liquidtemplate
    }> = [];

    for (const regex of regexes) {
      const regexInstance = new RegExp(regex.regex.source, regex.regex.flags);
      let match;
      while ((match = regexInstance.exec(text)) !== null) {
        matches.push({
          index: match.index,
          length: match[0].length,
          tokenType: regex.tokenType,
        });
      }
    }

    matches.sort((a, b) => a.index - b.index);

    const tokens = [];
    let prevLine = 0;
    let prevChar = 0;

    for (const match of matches) {
      const startPos = model.getPositionAt(match.index);

      // Calculate relative positions
      const line = startPos.lineNumber - 1; // 0-based line number
      const deltaLine = line - prevLine;
      const startChar = startPos.column - 1; // 0-based column
      const deltaStartChar =
        line === prevLine ? startChar - prevChar : startChar;

      const token = {
        deltaLine,
        deltaStartChar,
        length: match.length,
        tokenModifiers: 1, // Italic styling
        tokenType: match.tokenType,
      };
      tokens.push(token);

      // Update previous positions
      prevLine = line;
      prevChar = startChar;
    }

    const data = tokens.flatMap(
      ({ deltaLine, deltaStartChar, length, tokenModifiers, tokenType }) => [
        deltaLine,
        deltaStartChar,
        length,
        tokenType,
        tokenModifiers,
      ],
    );

    return {
      data: new Uint32Array(data),
    };
  },
  releaseDocumentSemanticTokens: (): void => {},
});
